
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { isMedianWebview } from '@/utils/webview-detection';
import { isOutdatedSafari } from '@/utils/device-detection';
import GoogleWebviewAuth from '@/components/auth/GoogleWebviewAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectAuthenticated?: boolean;
  redirectPath?: string;
  allowInactive?: boolean;
  requiredAccountTypes?: string[];
  requiresSubscription?: boolean;
}

const ProtectedRoute = ({ 
  children, 
  redirectAuthenticated = false,
  redirectPath = "/agenda",
  allowInactive = false,
  requiredAccountTypes = [],
  requiresSubscription = false
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const isWebview = isMedianWebview();
  
  // Show loading spinner or component while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Special handling for webview: if not authenticated and in webview, redirect to login
  if (isWebview && !isAuthenticated && location.pathname !== '/login' && location.pathname !== '/register' && location.pathname !== '/register/student' && location.pathname !== '/register/parent' && location.pathname !== '/register/teacher' && location.pathname !== '/forgot-password') {
    return <Navigate to="/login" replace />;
  }

  // If redirectAuthenticated is true and user is authenticated, redirect them
  if (redirectAuthenticated && isAuthenticated) {
    return <Navigate to={redirectPath} replace />;
  }
  
  // Redirect to login if not authenticated for protected routes
  if (!redirectAuthenticated && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirection if user needs to verify "account_checked"
  if (
    isAuthenticated &&
    user &&
    user.account_checked === 0 &&
    location.pathname !== '/account/check'
  ) {
    return <Navigate to="/account/check" replace />;
  }

  // Check if user account is inactive (is_active === 0)
  // Only redirect if this route doesn't explicitly allow inactive users
  if (
    isAuthenticated && 
    user && 
    user.is_active === 0 && 
    !allowInactive && 
    location.pathname !== '/verify'
  ) {
    return <Navigate to="/verify" replace />;
  }

  // Check if user has required account type
  if (
    isAuthenticated &&
    user &&
    requiredAccountTypes.length > 0 &&
    !requiredAccountTypes.includes(user.account_type)
  ) {
    return <Navigate to="/agenda" replace />;
  }

  // Check if route requires subscription
  if (
    isAuthenticated &&
    user &&
    requiresSubscription &&
    !user.is_subscribed &&
    !user.is_trialing
  ) {
    return <Navigate to="/subscription" replace />;
  }
  
  // Check for outdated Safari browser (only for authenticated users)
  if (
    isAuthenticated && 
    isOutdatedSafari() && 
    location.pathname !== '/update-browser'
  ) {
    return <Navigate to="/update-browser" replace />;
  }

  // Render the children
  return <>{children}</>;
};

export default ProtectedRoute;
